import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { ApiError } from '../api/client';
import { SHORT_CODE_TAKEN, SHORT_CODE_TAKEN_HINT, useCreateEvent, WEAK_PIN } from '../api/events';
import { useStripeAccounts } from '../api/stripeAccounts';
import { Field } from '../components/Field';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const PIN_RE = /^\d{6}$/;
const TML_RE = /^tml_[A-Za-z0-9]+$/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const TIMEZONES = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Toronto',
  'Europe/London',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

const createSchema = z
  .object({
    name: z.string().min(1, 'Required'),
    shortCode: z
      .string()
      .trim()
      .transform((s) => s.toUpperCase())
      .pipe(z.string().regex(/^[A-Z0-9]{1,8}$/, 'Letters and digits only, max 8')),
    startsOn: z.string().regex(ISO_DATE_RE, 'YYYY-MM-DD'),
    endsOn: z.string().regex(ISO_DATE_RE, 'YYYY-MM-DD'),
    timezone: z.string().min(1),
    stripeAccountId: z.string().regex(UUID_RE),
    stripeLocationId: z.string().regex(TML_RE, 'must match tml_xxx'),
    pin: z.string().regex(PIN_RE, 'Must be 6 digits'),
    venue: z.string().nullable(),
    city: z.string().nullable(),
  })
  .refine((d) => d.endsOn >= d.startsOn, {
    message: 'Must be on or after first day',
    path: ['endsOn'],
  });

type CreateValues = z.infer<typeof createSchema>;

const ERROR_LABELS: Record<string, string> = {
  stripe_account_not_found: "The selected Stripe account doesn't exist.",
  invalid_request: 'One of the fields is invalid.',
};

export function EventCreate() {
  const navigate = useNavigate();
  const createEvent = useCreateEvent();
  const stripeAccounts = useStripeAccounts();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      timezone: 'America/Toronto',
      venue: null,
      city: null,
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      const created = await createEvent.mutateAsync({
        name: values.name,
        shortCode: values.shortCode,
        startsOn: values.startsOn,
        endsOn: values.endsOn,
        timezone: values.timezone,
        stripeAccountId: values.stripeAccountId,
        stripeLocationId: values.stripeLocationId,
        pin: values.pin,
        venue: values.venue || null,
        city: values.city || null,
      });
      navigate(`/events/${created.id}/setup`, { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'short_code_taken') {
        setError('shortCode', { type: 'short_code_taken', message: SHORT_CODE_TAKEN });
      } else if (err instanceof ApiError && err.code === 'weak_pin') {
        setError('pin', { message: WEAK_PIN });
      } else if (err instanceof ApiError) {
        setError('root', {
          message: ERROR_LABELS[err.code] ?? 'Could not create the event.',
        });
      } else {
        setError('root', { message: "Couldn't reach the server." });
      }
    }
  });

  return (
    <div className="page">
      <div className="page-head">
        <h1>New event</h1>
        <div className="page-actions">
          <Link to="/events" className="btn btn-ghost">
            Cancel
          </Link>
        </div>
      </div>
      <form onSubmit={onSubmit} className="card card-pad">
        <Field label="Name" error={errors.name?.message}>
          <input type="text" autoComplete="off" {...register('name')} />
        </Field>
        <Field
          label="Short code"
          help={
            errors.shortCode?.type === 'short_code_taken'
              ? SHORT_CODE_TAKEN_HINT
              : 'Up to 8 characters; used in receipts and slugs.'
          }
          error={errors.shortCode?.message}
        >
          <input type="text" autoComplete="off" {...register('shortCode')} />
        </Field>

        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <Field label="First day" error={errors.startsOn?.message}>
              <input type="date" {...register('startsOn')} />
            </Field>
          </div>
          <div style={{ flex: 1 }}>
            <Field label="Last day" error={errors.endsOn?.message}>
              <input type="date" {...register('endsOn')} />
            </Field>
          </div>
        </div>

        <Field label="Timezone" error={errors.timezone?.message}>
          <select {...register('timezone')}>
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Stripe account"
          help="Pick the Stripe account this event's payments belong to."
          error={errors.stripeAccountId?.message}
        >
          <select {...register('stripeAccountId')}>
            <option value="">— Select —</option>
            {stripeAccounts.data?.map((a) => (
              <option key={a.id} value={a.id}>
                {a.displayName} ({a.label})
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Stripe Terminal location"
          help="tml_xxx — created in the Stripe dashboard under Terminal → Locations."
          error={errors.stripeLocationId?.message}
        >
          <input type="text" autoComplete="off" {...register('stripeLocationId')} />
        </Field>

        <Field
          label="Operator PIN"
          help="6 digits. Crew log in with the event code and this PIN."
          error={errors.pin?.message}
        >
          <input type="text" inputMode="numeric" autoComplete="off" {...register('pin')} />
        </Field>

        <Field label="Venue (optional)">
          <input type="text" autoComplete="off" {...register('venue')} />
        </Field>
        <Field label="City (optional)">
          <input type="text" autoComplete="off" {...register('city')} />
        </Field>

        {errors.root && <div className="form-error">{errors.root.message}</div>}
        <div className="page-actions">
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Creating…' : 'Create event'}
          </button>
        </div>
      </form>
    </div>
  );
}
